import axiosInstance from '../utils/axios';

/**
 * FAQ 관련 API 서비스
 * 
 * FAQ 모듈의 모든 원격 API 호출을 캡슐화합니다.
 * Notice 모듈과 동일한 패턴을 따릅니다.
 */
const faqService = {
  /**
   * FAQ 목록 조회
   * 
   * @returns {Promise<Object>} FAQ 목록 데이터
   */
  async getFAQList() {
    try {
      const response = await axiosInstance.get('/faq/list');

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || []
        };
      } else {
        console.error('FAQ 목록 조회 실패:', response.data.message);
        return {
          success: false,
          message: response.data.message || 'FAQ 목록 조회에 실패했습니다.'
        };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'FAQ 목록 조회 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * FAQ 상세 조회
   * 
   * @param {number} id FAQ ID
   * @returns {Promise<Object>} FAQ 상세 데이터
   */
  async getFAQDetail(id) {
    try {
      const response = await axiosInstance.get(`/faq/${id}`);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'FAQ 상세 조회에 실패했습니다.'
        };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'FAQ 상세 조회 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * FAQ 등록
   * 
   * @param {Object} faqData FAQ 데이터
   * @returns {Promise<Object>} 등록 결과
   */
  async createFAQ(faqData) {
    try {
      const response = await axiosInstance.post('/faq', faqData);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'FAQ가 성공적으로 등록되었습니다.'
        };
      } else {
        console.error('FAQ 등록 실패:', response.data.message);
        return {
          success: false,
          message: response.data.message || 'FAQ 등록에 실패했습니다.'
        };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'FAQ 등록 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * FAQ 수정
   * 
   * @param {number} id FAQ ID
   * @param {Object} faqData FAQ 데이터
   * @returns {Promise<Object>} 수정 결과
   */
  async updateFAQ(id, faqData) {
    try {
      const response = await axiosInstance.put(`/faq/${id}`, faqData);

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'FAQ가 성공적으로 수정되었습니다.'
        };
      } else {
        console.error('FAQ 수정 실패:', response.data.message);
        return {
          success: false,
          message: response.data.message || 'FAQ 수정에 실패했습니다.'
        };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'FAQ 수정 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * FAQ 삭제
   * 
   * @param {Array} idList 삭제할 FAQ ID 배열
   * @returns {Promise<Object>} 삭제 결과
   */
  async deleteFAQ(idList) {
    try {
      const response = await axiosInstance.delete('/faq/batch', {
        data: { checkIdxList: idList }
      });

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'FAQ가 성공적으로 삭제되었습니다.'
        };
      } else {
        console.error('FAQ 삭제 실패:', response.data.message);
        return {
          success: false,
          message: response.data.message || 'FAQ 삭제에 실패했습니다.'
        };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'FAQ 삭제 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  },

  /**
   * FAQ 순서 변경
   * 
   * @param {number} idx FAQ ID
   * @param {number} sequence 새 순서
   * @returns {Promise<Object>} 순서 변경 결과
   */
  async updateSequence(idx, sequence) {
    try {
      const response = await axiosInstance.patch('/faq/sequence', {
        idx,
        sequence
      });

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message || 'FAQ 순서가 성공적으로 변경되었습니다.'
        };
      } else {
        console.error('FAQ 순서 변경 실패:', response.data.message);
        return {
          success: false,
          message: response.data.message || 'FAQ 순서 변경에 실패했습니다.'
        };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'FAQ 순서 변경 중 오류가 발생했습니다.';
      return {
        success: false,
        message: errorMessage
      };
    }
  }
};

export default faqService;
